/* eslint-disable no-undef */

// Product Video Player Component (uses base VideoPlayer class)
class ProductVideoPlayer extends HTMLElement {
  constructor () {
    super()
    this.videoPlayer = null
  }

  connectedCallback () {
    this.setAttribute('tabindex', '0')

    // Initialize the base video player
    if (window.Tentbox?.VideoPlayer) {
      this.videoPlayer = new Tentbox.VideoPlayer(this)
      this.videoPlayer.initialize()
    } else {
      console.error('Tentbox.VideoPlayer not found - make sure global.js is loaded first')
    }
  }

  disconnectedCallback () {
    if (this.videoPlayer) {
      this.videoPlayer.destroy()
    }
  }

  // Expose video player methods for external use
  loadVideo () {
    return this.videoPlayer?.loadVideo()
  }

  playVideo () {
    return this.videoPlayer?.playVideo()
  }

  pauseVideo () {
    return this.videoPlayer?.pauseVideo()
  }

  resetVideo () {
    return this.videoPlayer?.resetVideo()
  }

  // Getters for state
  get videoLoaded () {
    return this.videoPlayer?.videoLoaded || false
  }

  get isLoading () {
    return this.videoPlayer?.isLoading || false
  }

  get isPlaying () {
    return this.videoPlayer?.isPlaying || false
  }

  // Method to switch to a new video source
  switchVideo (videoSrc, videoImage, title) {
    if (!this.videoPlayer) {
      return
    }

    // Update data attributes
    this.dataset.source = videoSrc
    this.dataset.alt = title

    // Update poster image and ensure it's visible
    const posterImg = this.querySelector('.video-poster')
    if (posterImg) {
      if (videoImage) {
        posterImg.src = videoImage
        posterImg.alt = title
      }
      // Reset poster image visibility
      posterImg.style.opacity = '1'
      posterImg.style.display = 'block'
    }

    // Reset video player state
    this.videoPlayer.resetVideo()

    // Clear video sources
    const video = this.querySelector('video')
    if (video) {
      const existingSources = video.querySelectorAll('source')
      existingSources.forEach(source => source.remove())
      video.currentTime = 0
      video.pause()
      // Hide video element so poster shows
      video.style.opacity = '0'
      video.style.zIndex = '0'
    }

    // Reset component state
    this.videoPlayer.videoLoaded = false
    this.videoPlayer.isLoading = false
    this.videoPlayer.isPlaying = false
    this.videoPlayer.hasBeenPlayed = false
    this.videoPlayer.wasPausedByUser = false

    // Remove loaded attributes
    this.removeAttribute('data-video-loaded')
    this.removeAttribute('data-has-been-played')
    this.removeAttribute('data-was-paused-by-user')

    // Show initial state
    this.videoPlayer.showPlayState()
    this.classList.add('video-not-started')
  }
}

// Product Videos Main Component (manages video list and player)
class ProductVideos extends HTMLElement {
  constructor () {
    super()
    this.currentVideoId = null
    this.videoData = new Map()
    this.activeButton = null
  }

  connectedCallback () {
    this.initializeComponent()
  }

  initializeComponent () {
    // Get all video items and store their data
    const videoItems = this.querySelectorAll('.product-videos__item')
    videoItems.forEach((item, index) => {
      const videoId = item.dataset.videoId
      const block = this.getBlockDataByIndex(index)

      if (videoId && block) {
        this.videoData.set(videoId, {
          id: videoId,
          title: block.title,
          runtime: block.runtime,
          videoSrc: block.videoSrc,
          videoImage: block.videoImage,
          element: item,
        })
      }
    })

    // Set up click listeners for video items
    this.setupVideoItemListeners()

    // Set the first video as current and active
    if (videoItems.length > 0) {
      const firstVideoId = videoItems[0].dataset.videoId
      this.setCurrentVideo(firstVideoId, false) // Don't auto-play on load
      this.setActiveItem(videoItems[0])
    }

    // Listen for video state changes from product-video-player
    this.setupVideoEventListeners()
  }

  getBlockDataByIndex (index) {
    // Extract data from DOM elements
    const item = this.querySelectorAll('.product-videos__item')[index]
    if (!item) return null

    return {
      title: item.querySelector('.product-videos__item-title').textContent.trim(),
      runtime: item.querySelector('.product-videos__item-runtime').textContent.trim(),
      videoSrc: item.dataset.videoSrc || '',
      videoImage: item.dataset.videoImage || '',
    }
  }

  setupVideoItemListeners () {
    const videoItems = this.querySelectorAll('.product-videos__item')

    videoItems.forEach((item, index) => {
      item.addEventListener('click', (e) => {
        e.preventDefault()
        const videoId = item.dataset.videoId
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

        if (videoId === this.currentVideoId) {
          // Same video - toggle play/pause
          this.toggleCurrentVideo()
        } else {
          // Different video - switch and play
          if (isMobile) {
            // On mobile, we need to call playVideo directly from user interaction to avoid autoplay restrictions
            this.switchToVideoMobile(videoId, item)
          } else {
            // Desktop can use the setTimeout approach
            this.switchToVideo(videoId, true)
            this.setActiveItem(item)
          }
        }
      })
    })
  }

  setupVideoEventListeners () {
    const videoPlayer = this.querySelector('product-video-player')
    if (!videoPlayer) return

    // Listen for video state changes
    videoPlayer.addEventListener('videoStateChange', (e) => {
      this.updateControlIcons(e.detail.state)
    })
  }

  setCurrentVideo (videoId, autoPlay = false) {
    const videoData = this.videoData.get(videoId)
    if (!videoData) {
      return
    }

    this.currentVideoId = videoId
    const videoPlayer = this.querySelector('product-video-player')

    if (videoPlayer) {
      // Check if this is the initial load and the video source already matches
      const isInitialLoad = !videoPlayer.videoPlayer?.hasBeenPlayed &&
                           videoPlayer.dataset.source === videoData.videoSrc &&
                           !videoPlayer.videoPlayer?.videoLoaded

      if (isInitialLoad) {
        // For initial load, just ensure the poster image is correct and set proper state
        if (videoData.videoImage) {
          const posterImg = videoPlayer.querySelector('.video-poster')
          if (posterImg) {
            posterImg.src = videoData.videoImage
            posterImg.alt = videoData.title
          }
        }

        // Ensure the video player shows the correct initial state
        if (videoPlayer.videoPlayer) {
          videoPlayer.videoPlayer.showPlayState()
          videoPlayer.classList.add('video-not-started')
        }
      } else {
        // Switch video in the player
        videoPlayer.switchVideo(videoData.videoSrc, videoData.videoImage, videoData.title)
      }

      if (autoPlay) {
        // Load and play the video after a brief delay
        setTimeout(() => {
          if (typeof videoPlayer.loadVideo === 'function') {
            videoPlayer.loadVideo()
            // Wait for video to load then play
            const checkAndPlay = () => {
              if (videoPlayer.videoLoaded && typeof videoPlayer.playVideo === 'function') {
                videoPlayer.playVideo()
              } else if (!videoPlayer.videoLoaded && !videoPlayer.isLoading) {
                setTimeout(checkAndPlay, 100)
              } else {
                // Video still loading, waiting...
              }
            }
            setTimeout(checkAndPlay, 200)
          }
        }, 100)
      }
    }

    this.updateControlIcons()
  }

  switchToVideo (videoId, autoPlay = false) {
    this.setCurrentVideo(videoId, autoPlay)
  }

  switchToVideoMobile (videoId, item) {
    const videoData = this.videoData.get(videoId)
    if (!videoData) {
      return
    }

    this.currentVideoId = videoId
    const videoPlayer = this.querySelector('product-video-player')

    if (!videoPlayer) {
      return
    }

    // Switch the video source
    videoPlayer.switchVideo(videoData.videoSrc, videoData.videoImage, videoData.title)

    // Set active item
    this.setActiveItem(item)

    // Update control icons
    this.updateControlIcons('loading')

    // Load and play video directly (no setTimeout to maintain user interaction chain)
    if (typeof videoPlayer.loadVideo === 'function') {
      // On mobile, mute video to help with autoplay restrictions
      const video = videoPlayer.querySelector('video')
      if (video) {
        video.muted = true
      }

      videoPlayer.loadVideo()

      // Attempt to play immediately if video is ready
      const attemptPlay = () => {
        if (videoPlayer.videoLoaded && typeof videoPlayer.playVideo === 'function') {
          videoPlayer.playVideo()
        } else if (!videoPlayer.isLoading) {
          // Video failed to load, try again
          videoPlayer.loadVideo()
          setTimeout(attemptPlay, 100)
        } else {
          // Still loading, wait a bit more
          setTimeout(attemptPlay, 50)
        }
      }

      // Start attempting to play immediately
      attemptPlay()
    }
  }

  toggleCurrentVideo () {
    const videoPlayer = this.querySelector('product-video-player')
    if (!videoPlayer) {
      return
    }

    if (!videoPlayer.videoLoaded && !videoPlayer.isLoading) {
      // Video not loaded yet, load and play
      if (typeof videoPlayer.loadVideo === 'function') {
        videoPlayer.loadVideo()
        const checkAndPlay = () => {
          if (videoPlayer.videoLoaded && typeof videoPlayer.playVideo === 'function') {
            videoPlayer.playVideo()
          } else if (!videoPlayer.videoLoaded && !videoPlayer.isLoading) {
            setTimeout(checkAndPlay, 100)
          }
        }
        setTimeout(checkAndPlay, 200)
      }
    } else if (videoPlayer.videoLoaded) {
      // Video loaded, toggle play/pause
      if (videoPlayer.isPlaying) {
        if (typeof videoPlayer.pauseVideo === 'function') {
          videoPlayer.pauseVideo()
        }
      } else {
        if (typeof videoPlayer.playVideo === 'function') {
          videoPlayer.playVideo()
        }
      }
    }
  }

  setActiveItem (item) {
    // Remove active class from all items
    const allItems = this.querySelectorAll('.product-videos__item')
    allItems.forEach(i => {
      i.classList.remove('product-videos__item--active')
      i.setAttribute('aria-pressed', 'false')
    })

    // Add active class to current item
    item.classList.add('product-videos__item--active')
    item.setAttribute('aria-pressed', 'true')
    this.activeButton = item
  }

  updateControlIcons (state = null) {
    if (!this.currentVideoId || !this.activeButton) return

    const videoPlayer = this.querySelector('product-video-player')
    if (!videoPlayer) return

    const activeItem = this.activeButton
    const playIcon = activeItem.querySelector('.product-videos__item-icon-play')
    const pauseIcon = activeItem.querySelector('.product-videos__item-icon-pause')
    const loadingIcon = activeItem.querySelector('.product-videos__item-icon-loading')

    // Hide all icons first
    playIcon?.classList.add('hidden')
    pauseIcon?.classList.add('hidden')
    loadingIcon?.classList.add('hidden')

    // Determine state from parameter or video player
    const currentState = state || (
      videoPlayer.isLoading
        ? 'loading'
        : videoPlayer.isPlaying ? 'playing' : 'ready'
    )

    switch (currentState) {
      case 'loading':
        loadingIcon?.classList.remove('hidden')
        break
      case 'playing':
        pauseIcon?.classList.remove('hidden')
        break
      default:
        playIcon?.classList.remove('hidden')
        break
    }
  }

  disconnectedCallback () {
    // Clean up any observers or listeners if needed
  }
}

// Register custom elements
if (!customElements.get('product-video-player')) {
  customElements.define('product-video-player', ProductVideoPlayer)
}

if (!customElements.get('product-videos')) {
  customElements.define('product-videos', ProductVideos)
}
